/**
 * Background Sync for Chunk Uploads
 *
 * Provides utilities for communicating with the upload service worker.
 * The SW reads chunks directly from OPFS (async API) to avoid duplicating storage.
 *
 * Flow:
 * 1. Main thread writes chunk to OPFS (handled by AudioRecorderProvider)
 * 2. Main thread calls registerChunkUpload() to notify SW
 * 3. SW reads from OPFS, uploads to Ambient, tracks status in OPFS
 */

/**
 * Post a message to the upload service worker with timeout
 * Non-blocking: if SW isn't ready within timeout, we continue without it
 */
async function postToServiceWorker(
  type: string,
  data: Record<string, unknown>,
  timeoutMs = 2000,
): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Add timeout to prevent hanging if SW never becomes ready
    const registrationPromise = navigator.serviceWorker.ready;
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), timeoutMs),
    );

    const registration = await Promise.race([
      registrationPromise,
      timeoutPromise,
    ]);

    if (!registration || !registration.active) {
      return;
    }
    registration.active.postMessage({ type, data });
  } catch {
    // Silent failure - SW not available
  }
}

/**
 * Register an upload session with the service worker
 *
 * Call this when starting a recording to map the OPFS session ID
 * to the Ambient session ID.
 */
export async function registerUploadSession(
  sessionId: string,
  ambientSessionId: string,
  organizationId: string,
  practiceId: string,
  synapseApiUrl: string,
): Promise<void> {
  await postToServiceWorker('REGISTER_SESSION', {
    sessionId,
    ambientSessionId,
    organizationId,
    practiceId,
    synapseApiUrl,
  });
}

/**
 * Notify the service worker that a chunk is ready for upload
 *
 * The chunk must already be written to OPFS. The SW will read it directly.
 * Passing the token inline avoids a race between separate CACHE_TOKEN and
 * UPLOAD_CHUNK messages (async SW event handlers don't serialize).
 */
export async function registerChunkUpload(
  sessionId: string,
  chunkIndex: number,
  token?: string,
): Promise<void> {
  // Use MessageChannel to wait for the SW to acknowledge it has registered
  // the chunk (incremented totalChunks). Without this, getPendingChunkCount
  // can race ahead of the SW processing the UPLOAD_CHUNK message and see 0.
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const activeWorker = registration.active;
    if (!activeWorker) return;

    await new Promise<void>((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => resolve();
      activeWorker.postMessage(
        { type: 'UPLOAD_CHUNK', data: { sessionId, chunkIndex, token } },
        [channel.port2],
      );
      // Timeout fallback — don't block forever if SW doesn't respond
      setTimeout(() => resolve(), 2000);
    });
  } catch {
    // Silent failure — chunk is in OPFS for recovery
  }
}

/**
 * Notify the service worker that recording is complete
 *
 * This allows the SW to finalize and clean up the session.
 */
export async function completeUploadSession(sessionId: string): Promise<void> {
  await postToServiceWorker('COMPLETE_SESSION', { sessionId });
}

/**
 * Cache the auth token in the service worker for uploads
 */
export async function cacheTokenInServiceWorker(token: string): Promise<void> {
  await postToServiceWorker('CACHE_TOKEN', { token });
}

/**
 * Trigger immediate queue processing in service worker
 */
export async function triggerQueueProcessing(): Promise<void> {
  await postToServiceWorker('PROCESS_QUEUE', {});
}

/**
 * Get count of pending chunks from service worker.
 *
 * @param sessionId - If provided, only count chunks for this OPFS session.
 *                    Otherwise counts across all sessions.
 */
export async function getPendingChunkCount(
  sessionId?: string,
): Promise<number> {
  if (!('serviceWorker' in navigator)) {
    return 0;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const activeWorker = registration.active;
    if (!activeWorker) return 0;

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data?.count ?? 0);
      };

      activeWorker.postMessage(
        { type: 'GET_PENDING_COUNT', data: { sessionId } },
        [channel.port2],
      );

      // Timeout fallback
      setTimeout(() => resolve(0), 1000);
    });
  } catch {
    return 0;
  }
}

/**
 * Check whether the active service worker is the videa-notes upload worker.
 *
 * Sends a PING message via MessageChannel and expects a response with
 * `{ type: 'videa-notes-sw' }`. Returns `false` when:
 *  - service workers aren't supported
 *  - the SW isn't ready / active
 *  - the active SW doesn't respond (e.g. MSW or wrong worker)
 *  - the response doesn't contain the expected identity marker
 */
export async function isUploadWorkerReachable(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);

    const activeWorker = registration?.active;
    if (!activeWorker) return false;

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(
          event.data?.type === 'videa-notes-sw' && event.data?.ok === true,
        );
      };
      activeWorker.postMessage({ type: 'PING', data: {} }, [channel.port2]);
      // Short timeout — if the SW doesn't reply it's not ours
      setTimeout(() => resolve(false), 2000);
    });
  } catch {
    return false;
  }
}
